<script setup lang="ts">
import { useConfigStore } from '~/store/useConfigStore'
import { useMenu } from '~/composables/useMenu'
import type { Menu } from '~/types'
import { useTickerInfo } from '~/composables/useTickerInfo'

const config = useConfigStore()

const { resolveMenuBySlug } = useMenu()

const footerMenuItems = computed<Menu.MenuItem[]>(() => resolveMenuBySlug('footer')?.menuItems || [])
const { ticker } = useTickerInfo()
</script>

<template>
  <footer class="footer">
    <div class="wrapper">
      <div class="footer__top">
        <nav class="footer__nav">
          <ul>
            <template v-for="menuItem in footerMenuItems">
              <Component
                :is="menuItem.component"
                v-if="menuItem.component && menuItem.enabled"
                :key="menuItem.id"
                :item="menuItem"
                class="footer__nav-menu-item"
              />
            </template>
          </ul>
        </nav>
        <IrEmailsFooterSubscribe v-if="ticker" />
      </div>
      <div class="footer__bottom">
        <div
          v-if="config.websiteCopyrightText"
          class="footer__copyright"
        >
          <p>{{ config.websiteCopyrightText }}</p>
        </div>
        <div class="footer__links">
          <a
            v-if="config.websiteTermsOfUseLink"
            :href="config.websiteTermsOfUseLink"
            target="_blank"
          >Terms of Use</a>
          <a
            v-if="config.websitePrivacyPolicyLink"
            :href="config.websitePrivacyPolicyLink"
            target="_blank"
          >Privacy Policy</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<style lang="scss">
.footer {
  @apply py-[40px] bg-dark-background;
  @apply md:py-[90px];

  .sr-only {
    @apply text-light-text;
  }

  &__top {
    @apply flex flex-col;
    @apply lg:flex-row;
  }

  &__nav {
    @apply flex justify-between w-full shrink-0 mb-[30px];
    @apply md:mb-[50px];
    @apply lg:w-1/2 lg:mb-0;

    > ul {
      @apply w-[60%];

      > li {
        @apply mb-[10px] last:mb-0;

        a,
        button {
          @apply text-[18px] text-light-text opacity-70 transition-opacity duration-300;
        }

        button {
          @apply cursor-default;
        }

        a {
          @apply hover:opacity-100;
        }

        > ul {
          @apply mt-[5px];

          li {
            ul {
              @apply list-disc marker:text-light-text flex flex-row flex-wrap gap-x-[10px] gap-y-[5px];

              li {
                @apply ml-[15px];
              }

              a,
              button {
                @apply text-[14px];
              }
            }
          }
        }
      }
    }
  }

  &__bottom {
    @apply flex flex-col items-center justify-center text-center mt-[30px] text-[14px] text-light-text;
    @apply md:mt-[60px] md:flex-row md:justify-between md:text-left;
  }

  &__copyright {
    @apply opacity-70 mb-[20px];
    @apply md:mb-0;
  }

  &__links {
    @apply flex gap-[50px];

    a {
      @apply opacity-70 transition-opacity duration-300;
      @apply hover:opacity-100;
    }
  }
}
</style>
